import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Users, TrendingUp, CheckCircle, RefreshCw, Loader2, Download, UserPlus,
  Clock, Linkedin, ExternalLink, Check, X, XCircle, Copy, Send, Calendar, Trash2,
  Activity, List, Settings, AlertTriangle, ChevronDown, Eye, BarChart2, Zap,
  PlayCircle, PauseCircle, Database, ArrowRight, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TCL_BANNER = 'https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/jxiucnvi_Third%20culture%20leadership%20banner.webp';

/* ── Sparkline ── */
const Sparkline = ({ data }) => {
  if (!data || data.length < 2) return null;
  const W = 100; const H = 40; const PAD = 3;
  const values = data.map(d => d.count);
  const maxV = Math.max(...values, 1);
  const pts = data.map((d, i) => ({
    x: PAD + (i / (data.length - 1)) * (W - PAD * 2),
    y: H - PAD - (d.count / maxV) * (H - PAD * 2),
    ...d,
  }));
  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
  const fillPath = `${linePath} L${pts[pts.length-1].x.toFixed(2)},${H} L${pts[0].x.toFixed(2)},${H} Z`;
  const DAY = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full" style={{ height: '52px' }}>
        <defs>
          <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#C99316" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#C99316" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillPath} fill="url(#spark-fill)" />
        <path d={linePath} fill="none" stroke="#C99316" strokeWidth="1.5" strokeOpacity="0.55" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={p.count > 0 ? 2.2 : 1.4}
            fill="#C99316" fillOpacity={p.count > 0 ? 0.85 : 0.2} />
        ))}
      </svg>
      <div className="flex justify-between mt-1">
        {data.map(d => (
          <span key={d.date} className="flex-1 text-center text-[9px]" style={{ color: 'rgba(250,248,243,0.18)' }}>
            {DAY[new Date(d.date + 'T12:00:00Z').getDay()]}
          </span>
        ))}
      </div>
    </div>
  );
};

const STAGE_SUBJECTS = {
  0:  'Enrolled — awaiting email 1',
  1:  'Email 1: Where does your cultural fluency begin?',
  2:  'Email 2: Your normal may not be universal',
  3:  'Email 3: Culture is operating even when nobody names it',
  4:  'Email 4: Did your message land as intended?',
  5:  'Email 5: Capability is not always received as capability',
  6:  'Email 6: Adapting does not mean disappearing',
  7:  'Email 7: Every room has a language',
  8:  "Email 8: Do not leave your team's culture to chance",
  9:  'Email 9: Clarity without humiliation',
  10: 'Email 10: Diversity is the beginning, not the destination',
  11: 'Email 11: Know what must not move',
  12: 'Email 12: What if their logic makes sense from where they stand?',
  13: 'Email 13: Translate the message, not your identity',
  14: 'Email 14: Build a third space',
  15: 'Email 15: Cultural fluency is never finished',
  16: 'Email 16: Respect Has More Than One Accent',
  17: 'Email 17: Before You Decide Someone Is Difficult',
  18: 'Email 18: The Meeting Has a Culture Too',
  19: 'Email 19: Who Actually Gets to Decide?',
  20: 'Email 20: Feedback Travels Through Culture',
  21: 'Email 21: The Extra Work Nobody Sees',
  22: 'Email 22: When Culture Fit Really Means Familiarity',
  23: 'Email 23: Trust Does Not Start in the Same Place',
  24: 'Email 24: Your "Common Sense" Had to Be Learned Somewhere',
  25: 'Email 25: Who Is Doing All the Adapting?',
  26: 'Email 26: Belonging Begins After the Welcome',
  27: 'Email 27: The Cost of Getting Silence Wrong',
  28: 'Email 28: Directness Is a Dial, Not a Switch',
  29: 'Email 29: The Person With the Best English May Not Have the Best Idea',
  30: 'Email 30: Who Speaks Last Matters',
  31: 'Email 31: Dignity Changes the Conversation',
  32: 'Email 32: Can Your Team Disagree Well?',
  33: 'Email 33: One Cultural Habit Worth Watching This Month',
  34: 'Email 34: The Leadership Cost of Being Too Certain',
  35: 'Email 35: When Warmth Looks Unprofessional',
  36: 'Email 36: When Distance Looks Uncaring',
  37: 'Email 37: Power Is Present Even When Nobody Mentions It',
  38: 'Email 38: The Question Behind the Question',
  39: 'Email 39: You Can Be Right and Still Lose the Room',
  40: 'Email 40: Cultural Fluency Is Built in Small Moments',
  41: 'Email 41: What Happens After the Misunderstanding?',
  42: 'Email 42: Remote Work Still Has a Culture',
  43: 'Email 43: A Shared Culture Should Be Built Deliberately',
  44: 'Email 44: What Does Your Team Reward?',
  45: 'Email 45: Confidence Has Cultural Accents',
  46: 'Email 46: What Are You Asking People to Leave at the Door?',
  47: 'Email 47: Four Questions for a Difficult Room',
  48: 'Email 48: Revisit Your Cultural Fluency',
  49: 'Email 49: What Have You Started Seeing Differently?',
  50: 'Email 50: Keep Becoming Fluent (sequence complete)',
};

const PHASE_LABELS = {
  1: 'Bi-weekly', 2: 'Bi-weekly', 3: 'Bi-weekly', 4: 'Bi-weekly',
  5: 'Bi-weekly', 6: 'Bi-weekly', 7: 'Bi-weekly', 8: 'Bi-weekly',
  9: 'Bi-weekly', 10: 'Bi-weekly', 11: 'Bi-weekly', 12: 'Bi-weekly',
};
const getPhaseLabel = (stage) => {
  if (stage <= 12) return 'Bi-weekly';
  if (stage <= 32) return 'Weekly';
  return 'Monthly';
};
const getPhaseColor = (stage) => {
  if (stage <= 12) return 'text-blue-400/70';
  if (stage <= 32) return 'text-teal-400/70';
  return 'text-gold/70';
};

const getCsrf = () => {
  const m = document.cookie.split('; ').find(r => r.startsWith('tcl_csrf='));
  return m ? m.split('=')[1] : '';
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const AdminNurture = () => {
  // ── Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // ── Overview state
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mailingStats, setMailingStats] = useState(null);
  const [recentSubs, setRecentSubs] = useState([]);
  const [sparkDays, setSparkDays] = useState([]);
  const [sourceBreakdown, setSourceBreakdown] = useState({});
  const [mailingLoading, setMailingLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // ── Email preview state
  const [previewStage, setPreviewStage] = useState(1);
  const [cfqCompleted, setCfqCompleted] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // ── Manual send state
  const [manualStage, setManualStage] = useState(1);
  const [manualTarget, setManualTarget] = useState('specific');
  const [manualEmail, setManualEmail] = useState('');
  const [manualGroupStage, setManualGroupStage] = useState(0);
  const [manualOverride, setManualOverride] = useState(false);
  const [manualSending, setManualSending] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  // Schedule mode
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleTime, setScheduleTime] = useState(() => {
    // Default to tomorrow 09:00 London time
    const d = new Date(); d.setDate(d.getDate() + 1);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T09:00`;
  });

  // ── Scheduled sends
  const [scheduledSends, setScheduledSends] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  // ── Social modal
  const [socialModal, setSocialModal] = useState({ open: false, text: '', subject: '' });
  const [savingDraft, setSavingDraft] = useState(false);
  const [savedDraft, setSavedDraft] = useState(false);
  const [copiedText, setCopiedText] = useState(false);
  const [loadingSocial, setLoadingSocial] = useState(false);

  // ── Health
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  // ── Queue
  const [queue, setQueue] = useState(null);
  const [queueLoading, setQueueLoading] = useState(false);

  // ── Email Library / stats
  const [emailStats, setEmailStats] = useState({});
  const [emailStatsLoading, setEmailStatsLoading] = useState(false);
  const [libraryPreview, setLibraryPreview] = useState(null); // stage number

  // ── Delivery Log
  const [deliveryLog, setDeliveryLog] = useState([]);
  const [deliveryLogTotal, setDeliveryLogTotal] = useState(0);
  const [deliveryLogPage, setDeliveryLogPage] = useState(1);
  const [deliveryLogLoading, setDeliveryLogLoading] = useState(false);
  const [logEmailFilter, setLogEmailFilter] = useState('');
  const [logStageFilter, setLogStageFilter] = useState('');
  const [logStatusFilter, setLogStatusFilter] = useState('');
  const [exportingLog, setExportingLog] = useState(false);

  // ── Phase Config / Settings
  const [phaseConfig, setPhaseConfig] = useState(null);
  const [phaseConfigForm, setPhaseConfigForm] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  // ── Email Events (opens / clicks)
  const [emailEvents, setEmailEvents] = useState(null);
  const [emailEventsLoading, setEmailEventsLoading] = useState(false);

  // ── Re-engagement
  const [inactiveStats, setInactiveStats] = useState(null);
  const [inactiveLoading, setInactiveLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [reactivateResult, setReactivateResult] = useState(null);

  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  // ═══════════════════════════════════════════════════════════
  // Fetch functions
  // ═══════════════════════════════════════════════════════════

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/stats`, { headers, credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch {}
    setLoading(false);
  };

  const fetchMailingData = useCallback(async () => {
    setMailingLoading(true);
    try {
      const [statsRes, listRes] = await Promise.all([
        fetch(`${API_URL}/api/cfq/admin/stats`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/cfq/admin/mailing-list?page=1&page_size=8`, { headers, credentials: 'include' }),
      ]);
      if (statsRes.ok) {
        const s = await statsRes.json();
        setMailingStats({ total: s.mailing_list_count ?? 0, confirmed: s.mailing_confirmed ?? 0, pending: s.mailing_pending ?? 0, new_week: s.new_subscribers ?? 0 });
        setSparkDays(s.mailing_by_day ?? []);
        setSourceBreakdown(s.source_breakdown ?? {});
      }
      if (listRes.ok) { const l = await listRes.json(); setRecentSubs(l.subscribers ?? []); }
    } catch {}
    setMailingLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/preview/${previewStage}?cfq_completed=${cfqCompleted}`, { headers, credentials: 'include' });
      if (res.ok) setPreviewHtml(await res.text());
    } catch {}
    setPreviewLoading(false);
  };

  const fetchScheduledSends = async () => {
    setScheduledLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/scheduled-sends`, { headers, credentials: 'include' });
      if (res.ok) setScheduledSends(await res.json());
    } catch {}
    setScheduledLoading(false);
  };

  const fetchHealth = async () => {
    setHealthLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/health`, { headers, credentials: 'include' });
      if (res.ok) setHealth(await res.json());
    } catch {}
    setHealthLoading(false);
  };

  const fetchQueue = async () => {
    setQueueLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/queue`, { headers, credentials: 'include' });
      if (res.ok) setQueue(await res.json());
    } catch {}
    setQueueLoading(false);
  };

  const fetchEmailStats = async () => {
    setEmailStatsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/email-stats`, { headers, credentials: 'include' });
      if (res.ok) { const d = await res.json(); setEmailStats(d.by_stage || {}); }
    } catch {}
    setEmailStatsLoading(false);
  };

  const fetchDeliveryLog = async (page = 1, emailF = logEmailFilter, stageF = logStageFilter, statusF = logStatusFilter) => {
    setDeliveryLogLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 50 });
      if (emailF) params.set('email_filter', emailF);
      if (stageF !== '' && stageF !== null) params.set('stage_filter', stageF);
      if (statusF) params.set('status_filter', statusF);
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/delivery-log?${params}`, { headers, credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setDeliveryLog(d.logs || []);
        setDeliveryLogTotal(d.total || 0);
        setDeliveryLogPage(page);
      }
    } catch {}
    setDeliveryLogLoading(false);
  };

  const handleLogExportCsv = async () => {
    setExportingLog(true);
    try {
      const params = new URLSearchParams();
      if (logEmailFilter) params.set('email_filter', logEmailFilter);
      if (logStageFilter !== '') params.set('stage_filter', logStageFilter);
      if (logStatusFilter) params.set('status_filter', logStatusFilter);
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/delivery-log/export.csv?${params}`, { headers, credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `delivery-log-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {}
    setExportingLog(false);
  };

  const fetchPhaseConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/phase-config`, { headers, credentials: 'include' });
      if (res.ok) { const d = await res.json(); setPhaseConfig(d); setPhaseConfigForm(d); }
    } catch {}
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/admin/mailing-export`, { headers, credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `mailing-list-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
        URL.revokeObjectURL(url);
      }
    } catch {}
    setExporting(false);
  };

  /** Convert a datetime-local string entered as Europe/London time → UTC ISO string */
  const londonToUTC = (localStr) => {
    const [date, time = '00:00'] = localStr.split('T');
    const [yr, mo, dy] = date.split('-').map(Number);
    const [hr, mn] = time.split(':').map(Number);
    // Find the London UTC offset by checking what hour London shows at noon UTC on that day
    const refUtc = new Date(Date.UTC(yr, mo - 1, dy, 12, 0, 0));
    const londonNoon = parseInt(
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', hour12: false }).format(refUtc)
    );
    const offsetHours = londonNoon - 12; // +1 BST, 0 GMT
    return new Date(Date.UTC(yr, mo - 1, dy, hr - offsetHours, mn, 0)).toISOString();
  };

  /** Determine current London UTC offset label (GMT / BST) */
  const londonOffsetLabel = (() => {
    const d = new Date();
    const h = parseInt(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', hour12: false }).format(d));
    const offset = h - d.getUTCHours();
    return offset >= 1 ? 'BST (UTC+1)' : 'GMT (UTC+0)';
  })();

  const handleScheduleSend = async () => {
    if (!scheduleTime) return;
    const target = manualTarget === 'specific' ? manualEmail.trim()
      : manualTarget === 'all_unstarted' ? 'all_unstarted'
      : `all_at_stage:${manualGroupStage}`;
    if (manualTarget === 'specific' && !target) { setManualResult({ ok: false, message: 'Enter a subscriber email' }); return; }
    // Schedule-send only supports single subscriber
    if (manualTarget !== 'specific') { setManualResult({ ok: false, message: 'Scheduled send is only available for a specific subscriber' }); return; }
    setManualSending(true);
    setManualResult(null);
    try {
      const sendAt = londonToUTC(scheduleTime);
      const res = await fetch(`${API_URL}/api/cfq/nurture/schedule-send`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ stage: manualStage, target, send_at: sendAt, override_stage: manualOverride }),
      });
      const d = await res.json();
      if (res.ok) {
        const londonDisplay = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', dateStyle: 'medium', timeStyle: 'short' }).format(new Date(sendAt));
        setManualResult({ ok: true, sent: 0, total: 1, message: `Scheduled for ${londonDisplay} London time` });
        fetchScheduledSends();
      } else {
        setManualResult({ ok: false, message: d.detail || 'Failed to schedule' });
      }
    } catch (e) {
      setManualResult({ ok: false, message: e.message });
    }
    setManualSending(false);
  };

  const handleManualSend = async () => {
    let target = '';
    if (manualTarget === 'specific') {
      target = manualEmail.trim();
      if (!target) { alert('Please enter an email address.'); return; }
    } else if (manualTarget === 'all_unstarted') {
      target = 'all_unstarted';
    } else if (manualTarget === 'all_at_stage') {
      target = `all_at_stage:${manualGroupStage}`;
    }
    setManualSending(true);
    setManualResult(null);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/manual-send`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
        credentials: 'include',
        body: JSON.stringify({ stage: manualStage, target, override_stage: manualOverride }),
      });
      const d = await res.json();
      setManualResult(res.ok ? { ok: true, ...d } : { ok: false, message: d.detail || 'Failed' });
      if (res.ok) fetchScheduledSends();
    } catch (e) {
      setManualResult({ ok: false, message: e.message });
    }
    setManualSending(false);
  };

  const handleTargetChange = (val) => {
    setManualTarget(val);
    if (val === 'all_unstarted' || val === 'all_at_stage') setManualOverride(true);
    else setManualOverride(false);
  };

  const handleCancelScheduled = async (id) => {
    setCancellingId(id);
    try {
      await fetch(`${API_URL}/api/cfq/nurture/scheduled-send/${id}`, { method: 'DELETE', headers, credentials: 'include' });
      setScheduledSends(prev => prev.filter(s => s.id !== id));
    } catch {}
    setCancellingId(null);
  };

  const handleOpenSocialModal = async () => {
    setLoadingSocial(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/to-social/${previewStage}`, { headers, credentials: 'include' });
      if (!res.ok) throw new Error('Failed to build post');
      const { linkedin_text, subject } = await res.json();
      setSocialModal({ open: true, text: linkedin_text, subject: subject || STAGE_SUBJECTS[previewStage] });
      setSavedDraft(false);
    } catch {}
    setLoadingSocial(false);
  };

  const closeSocialModal = () => setSocialModal({ open: false, text: '', subject: '' });

  const handlePublishLinkedIn = () => window.open(`https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(socialModal.text)}`, '_blank', 'noopener,noreferrer');
  const handlePublishX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(socialModal.text.slice(0, 280))}`, '_blank', 'noopener,noreferrer');

  const handleCopyText = async () => {
    try { await navigator.clipboard.writeText(socialModal.text); setCopiedText(true); setTimeout(() => setCopiedText(false), 2000); } catch {}
  };

  const handleSaveDraftCMS = async () => {
    setSavingDraft(true);
    try {
      const res = await fetch(`${API_URL}/api/cms/social`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
        credentials: 'include',
        body: JSON.stringify({ content: socialModal.text, post_type: 'long', platforms: ['linkedin', 'twitter'], status: 'draft', utm_link: null }),
      });
      if (res.ok) setSavedDraft(true);
    } catch {}
    setSavingDraft(false);
  };

  const handleSavePhaseConfig = async () => {
    setSavingConfig(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/phase-config`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrf() },
        credentials: 'include',
        body: JSON.stringify(phaseConfigForm),
      });
      if (res.ok) { setPhaseConfig(phaseConfigForm); setConfigSaved(true); setTimeout(() => setConfigSaved(false), 3000); }
    } catch {}
    setSavingConfig(false);
  };

  const handleReconcile = async () => {
    if (!window.confirm('This will backfill next_send_at for all confirmed subscribers who are missing it. Stage-0 subscribers will be staggered 10/day from tomorrow. No stages will be reset. Continue?')) return;
    setReconcileLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/reconcile`, {
        method: 'POST',
        headers: { ...headers, 'X-CSRF-Token': getCsrf() },
        credentials: 'include',
      });
      if (res.ok) { const d = await res.json(); setReconcileResult(d); }
    } catch {}
    setReconcileLoading(false);
  };

  const fetchEmailEvents = async () => {
    setEmailEventsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/email-events`, { headers });
      if (res.ok) setEmailEvents(await res.json());
    } catch {}
    setEmailEventsLoading(false);
  };

  const fetchInactiveStats = async () => {
    setInactiveLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/inactive-stats`, { headers });
      if (res.ok) setInactiveStats(await res.json());
    } catch {}
    setInactiveLoading(false);
  };

  const handleReactivate = async () => {
    if (!window.confirm('This will re-queue inactive subscribers (90+ days) and any never-started subscribers for the next cron fire. No emails will be sent immediately. Continue?')) return;
    setReactivateLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/cfq/nurture/admin/reactivate-inactive`, {
        method: 'POST',
        headers: { ...headers, 'X-CSRF-Token': getCsrf() },
        credentials: 'include',
      });
      if (res.ok) setReactivateResult(await res.json());
    } catch {}
    setReactivateLoading(false);
  };

  // ── Effects
  useEffect(() => {
    fetchStats();
    fetchMailingData();
    fetchScheduledSends();
    fetchHealth();
  }, [fetchMailingData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchPreview(); }, [previewStage, cfqCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'queue') fetchQueue();
    if (activeTab === 'library') fetchEmailStats();
    if (activeTab === 'log') { fetchDeliveryLog(1); fetchEmailEvents(); }
    if (activeTab === 'settings') { fetchPhaseConfig(); fetchInactiveStats(); }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxCount = stats ? Math.max(...stats.stages.map(s => s.count), 1) : 1;

  const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'queue', label: 'Queue & Health', icon: Zap },
    { id: 'library', label: 'Email Library', icon: Mail },
    { id: 'log', label: 'Delivery Log', icon: List },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  // ── Health status colour
  const healthColor = { healthy: '#4ade80', degraded: '#fb923c', warning: '#f87171', no_data: 'rgba(250,248,243,0.3)' };
  const healthLabel = { healthy: 'Healthy', degraded: 'Degraded', warning: 'Warning — cron may be stalled', no_data: 'No data yet' };

  return (
    <>
    <div data-testid="admin-nurture">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-serif text-2xl text-cream font-semibold">Nurture &amp; Mailing List</h1>
          <p className="text-cream/30 text-sm mt-1">Cultural Fluency Development Journey — 50 emails</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Health indicator */}
          {health && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cream/[0.06] bg-navy" title={healthLabel[health.status] || ''}>
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: healthColor[health.status] || 'rgba(250,248,243,0.3)', boxShadow: health.status === 'healthy' ? '0 0 6px rgba(74,222,128,0.5)' : 'none' }} />
              <span className="text-[10px] text-cream/40">{healthLabel[health.status]}</span>
            </div>
          )}
          <button onClick={() => { fetchStats(); fetchMailingData(); fetchHealth(); }} disabled={loading || mailingLoading}
            className="p-2 text-cream/30 hover:text-gold border border-cream/10 rounded-lg disabled:opacity-50"
            data-testid="nurture-refresh-btn">
            <RefreshCw size={16} className={(loading || mailingLoading) ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 border-b border-gold/[0.06] pb-px overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            data-testid={`tab-${id}`}
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-medium whitespace-nowrap transition-all rounded-t-lg"
            style={{
              color: activeTab === id ? '#C99316' : 'rgba(250,248,243,0.35)',
              background: activeTab === id ? 'rgba(201,147,22,0.07)' : 'transparent',
              borderBottom: activeTab === id ? '2px solid #C99316' : '2px solid transparent',
            }}
          >
            <Icon size={13} />
            {label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: OVERVIEW ═══════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <>
          {loading ? (
            <div className="py-16 flex items-center justify-center text-cream/20">
              <Loader2 size={24} className="animate-spin mr-2" />
              <span className="text-sm">Loading stats…</span>
            </div>
          ) : stats ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Confirmed',      value: stats.confirmed,           icon: Users,       color: 'text-cream' },
                  { label: 'CFQ Completed',  value: stats.cfq_completed,       icon: TrendingUp,  color: 'text-gold' },
                  { label: 'Active',         value: stats.active_in_sequence,  icon: Mail,        color: 'text-emerald-400' },
                  { label: 'Sequence Done',  value: stats.sequence_completed,  icon: CheckCircle, color: 'text-blue-400' },
                ].map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className="bg-navy border border-gold/[0.06] rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-cream/25 text-[10px] uppercase tracking-wider">{label}</p>
                      <Icon size={14} className={`${color} opacity-50`} />
                    </div>
                    <p className={`text-3xl font-serif font-bold ${color}`}>{value}</p>
                  </div>
                ))}
              </div>

              {/* Stage Distribution */}
              <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-8" data-testid="nurture-stage-distribution">
                <h2 className="text-cream/50 text-[10px] font-semibold uppercase tracking-wider mb-5">
                  Subscribers by Stage
                </h2>
                <div className="space-y-2.5">
                  {stats.stages.map(({ stage, count }) => (
                    <div key={stage} className="flex items-center gap-3" data-testid={`nurture-stage-${stage}`}>
                      <span className="text-cream/20 text-[11px] font-mono w-5 text-right flex-shrink-0">{stage}</span>
                      <div className="flex-1 relative">
                        <div className="bg-cream/[0.04] rounded-full h-4 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: count > 0 ? `${Math.max(2, (count / maxCount) * 100)}%` : '0%',
                              backgroundColor: stage === 50 ? 'rgba(201,147,22,0.5)' : stage === 0 ? 'rgba(250,248,243,0.12)' : 'rgba(201,147,22,0.25)',
                            }} />
                        </div>
                      </div>
                      <span className="text-cream/35 text-xs w-6 text-right flex-shrink-0 tabular-nums">{count}</span>
                      <span className="text-cream/15 text-[10px] hidden lg:block w-80 truncate">{STAGE_SUBJECTS[stage]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-cream/20 text-sm">Unable to load stats</div>
          )}

          {/* Mailing List Overview */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-8" data-testid="mailing-list-overview">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-cream/50 text-[10px] font-semibold uppercase tracking-wider">Mailing List</h2>
              <button onClick={handleExport} disabled={exporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider border border-cream/10 rounded-lg text-cream/30 hover:text-gold hover:border-gold/30 transition-colors disabled:opacity-40"
                data-testid="mailing-export-btn">
                {exporting ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                Export CSV
              </button>
            </div>
            {mailingLoading ? (
              <div className="py-8 flex items-center justify-center text-cream/20">
                <Loader2 size={18} className="animate-spin mr-2" /><span className="text-xs">Loading…</span>
              </div>
            ) : mailingStats ? (
              <>
                {sparkDays.length > 0 && (
                  <div className="bg-cream/[0.025] border border-cream/[0.05] rounded-lg px-4 pt-4 pb-3 mb-5" data-testid="mailing-sparkline">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(250,248,243,0.25)' }}>7-Day Growth</p>
                      <p className="text-[11px] tabular-nums" style={{ color: 'rgba(201,147,22,0.65)' }}>+{sparkDays.reduce((a, d) => a + d.count, 0)} this week</p>
                    </div>
                    <Sparkline data={sparkDays} />
                    {Object.keys(sourceBreakdown).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-cream/[0.04]">
                        {[
                          { key: 'homepage', label: 'Homepage', color: 'rgba(99,179,237,0.7)' },
                          { key: 'article', label: 'Article', color: 'rgba(201,147,22,0.7)' },
                          { key: 'footer', label: 'Footer', color: 'rgba(154,205,150,0.7)' },
                          { key: 'unknown', label: 'Unknown', color: 'rgba(250,248,243,0.2)' },
                        ].filter(s => sourceBreakdown[s.key] > 0).map(({ key, label, color }) => (
                          <span key={key} className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full border" style={{ borderColor: color.replace('0.7', '0.25'), color }} data-testid={`source-pill-${key}`}>
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                            {label} <span className="tabular-nums opacity-80">{sourceBreakdown[key]}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Total Subscribers', value: mailingStats.total, icon: Users, color: 'text-cream' },
                    { label: 'Confirmed', value: mailingStats.confirmed, icon: CheckCircle, color: 'text-emerald-400' },
                    { label: 'Awaiting Confirm', value: mailingStats.pending, icon: Clock, color: 'text-amber-400' },
                    { label: 'New This Week', value: mailingStats.new_week, icon: UserPlus, color: 'text-gold' },
                  ].map(({ label, value, icon: Icon, color }) => (
                    <div key={label} className="bg-cream/[0.03] border border-cream/[0.06] rounded-lg p-4" data-testid={`mailing-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-cream/25 text-[10px] uppercase tracking-wider leading-tight">{label}</p>
                        <Icon size={13} className={`${color} opacity-50`} />
                      </div>
                      <p className={`text-2xl font-serif font-bold ${color}`}>{value}</p>
                    </div>
                  ))}
                </div>
                {recentSubs.length > 0 && (
                  <div>
                    <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-3">Recent Sign-Ups</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="recent-subscribers-table">
                        <thead>
                          <tr className="border-b border-cream/[0.06]">
                            {['Name','Email','Status','Joined'].map(h => (
                              <th key={h} className={`text-left text-[10px] uppercase tracking-wider text-cream/20 pb-2 pr-4 font-normal ${h === 'Status' ? 'hidden sm:table-cell' : ''} ${h === 'Joined' ? 'hidden md:table-cell' : ''}`}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {recentSubs.map(sub => (
                            <tr key={sub.email} className="border-b border-cream/[0.03] hover:bg-cream/[0.02] transition-colors" data-testid="subscriber-row">
                              <td className="py-2.5 pr-4 text-cream/60 text-xs">{sub.first_name || <span className="text-cream/20">—</span>}</td>
                              <td className="py-2.5 pr-4 text-cream/50 text-xs font-mono">{sub.email}</td>
                              <td className="py-2.5 pr-4 hidden sm:table-cell">
                                <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full ${sub.confirmed ? 'bg-emerald-500/10 text-emerald-400' : 'bg-amber-500/10 text-amber-400'}`}>
                                  {sub.confirmed ? 'Confirmed' : 'Pending'}
                                </span>
                              </td>
                              <td className="py-2.5 text-cream/25 text-xs hidden md:table-cell tabular-nums">{fmtDate(sub.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-cream/15 text-[10px] mt-3">Showing 8 most recent. Use Export CSV for full list.</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-cream/20 text-xs text-center py-4">Unable to load mailing data.</p>
            )}
          </div>

          {/* Email Preview */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6" data-testid="nurture-email-preview">
            <h2 className="text-cream/50 text-[10px] font-semibold uppercase tracking-wider mb-5">Email Preview</h2>
            <div className="flex flex-wrap items-center gap-4 mb-5">
              <div>
                <label className="block text-cream/20 text-[10px] uppercase tracking-wider mb-1">Stage</label>
                <select value={previewStage} onChange={e => setPreviewStage(Number(e.target.value))}
                  className="bg-navy-deep border border-cream/10 rounded-lg px-4 py-2 text-cream text-sm focus:outline-none focus:border-gold/30"
                  data-testid="preview-stage-select">
                  {Array.from({ length: 50 }, (_, i) => i + 1).map(s => <option key={s} value={s}>Stage {s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-cream/20 text-[10px] uppercase tracking-wider mb-1">Variant</label>
                <label className="flex items-center gap-2 text-cream/40 text-sm cursor-pointer border border-cream/10 rounded-lg px-4 py-2 hover:border-gold/20">
                  <input type="checkbox" checked={cfqCompleted} onChange={e => setCfqCompleted(e.target.checked)} className="rounded" data-testid="preview-cfq-completed" />
                  CFQ Completed variant
                </label>
              </div>
              {previewLoading && <div className="flex items-center gap-2 text-cream/25 text-xs mt-5"><Loader2 size={14} className="animate-spin" />Loading preview…</div>}
            </div>
            <p className="text-cream/20 text-xs mb-3 truncate">{STAGE_SUBJECTS[previewStage]}</p>
            {previewHtml && !previewLoading && (
              <div className="rounded-xl overflow-hidden border border-cream/[0.06]">
                <iframe srcDoc={previewHtml} title={`Email preview — Stage ${previewStage}`} className="w-full" style={{ height: '640px', border: 'none' }} data-testid="nurture-preview-iframe" />
              </div>
            )}
            {previewHtml && !previewLoading && (
              <div className="mt-4">
                <button onClick={handleOpenSocialModal} disabled={loadingSocial}
                  className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #0A66C2 0%, #000000 100%)', color: '#fff', border: '1px solid rgba(255,255,255,0.08)' }}
                  data-testid="publish-social-btn">
                  {loadingSocial ? <Loader2 size={15} className="animate-spin" /> : <><Linkedin size={14} /><span style={{ opacity: 0.5, fontSize: '11px', margin: '0 1px' }}>/</span><X size={13} /></>}
                  {loadingSocial ? 'Building post…' : 'Publish to LinkedIn / X'}
                </button>
              </div>
            )}
          </div>

          {/* Manual Send Panel */}
          <div className="mt-4 rounded-2xl overflow-hidden" style={{ background: 'rgba(250,248,243,0.03)', border: '1px solid rgba(250,248,243,0.06)' }}>
            <div className="px-5 py-4 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(250,248,243,0.05)' }}>
              <Send size={13} style={{ color: '#C99316' }} />
              <h2 className="text-sm font-medium" style={{ color: 'rgba(250,248,243,0.8)' }}>Manual Send</h2>
              <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(201,147,22,0.1)', color: '#C99316' }}>Push any email 1–50 to a subscriber or group — sends immediately</span>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-xs" style={{ color: 'rgba(250,248,243,0.4)', minWidth: '60px' }}>Email stage</label>
                <select value={manualStage} onChange={e => setManualStage(Number(e.target.value))}
                  className="px-3 py-1.5 rounded-lg text-xs"
                  style={{ background: 'rgba(250,248,243,0.06)', border: '1px solid rgba(250,248,243,0.08)', color: 'rgba(250,248,243,0.8)', maxWidth: '340px' }}
                  data-testid="manual-stage-select">
                  {Object.entries(STAGE_SUBJECTS).filter(([n]) => Number(n) >= 1).map(([n, label]) => (
                    <option key={n} value={n}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-xs" style={{ color: 'rgba(250,248,243,0.4)', minWidth: '60px' }}>Send to</label>
                <div className="flex gap-2 flex-wrap">
                  {[{ val: 'specific', label: 'Specific subscriber' }, { val: 'all_unstarted', label: 'All unstarted' }, { val: 'all_at_stage', label: 'All at stage N' }].map(opt => (
                    <button key={opt.val} onClick={() => handleTargetChange(opt.val)}
                      className="px-3 py-1.5 rounded-lg text-xs transition-all"
                      style={{ background: manualTarget === opt.val ? 'rgba(201,147,22,0.15)' : 'rgba(250,248,243,0.05)', border: `1px solid ${manualTarget === opt.val ? 'rgba(201,147,22,0.4)' : 'rgba(250,248,243,0.07)'}`, color: manualTarget === opt.val ? '#C99316' : 'rgba(250,248,243,0.45)' }}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>
              {manualTarget === 'specific' && (
                <div className="flex items-center gap-3">
                  <label className="text-xs" style={{ color: 'rgba(250,248,243,0.4)', minWidth: '60px' }}>Email</label>
                  <input type="email" value={manualEmail} onChange={e => setManualEmail(e.target.value)} placeholder="subscriber@example.com"
                    className="px-3 py-1.5 rounded-lg text-xs flex-1 max-w-xs"
                    style={{ background: 'rgba(250,248,243,0.06)', border: '1px solid rgba(250,248,243,0.08)', color: 'rgba(250,248,243,0.8)' }}
                    data-testid="manual-email-input" />
                </div>
              )}
              {manualTarget === 'all_at_stage' && (
                <div className="flex items-center gap-3">
                  <label className="text-xs" style={{ color: 'rgba(250,248,243,0.4)', minWidth: '60px' }}>At stage</label>
                  <input type="number" min="0" max="50" value={manualGroupStage} onChange={e => setManualGroupStage(Number(e.target.value))}
                    className="px-3 py-1.5 rounded-lg text-xs w-20"
                    style={{ background: 'rgba(250,248,243,0.06)', border: '1px solid rgba(250,248,243,0.08)', color: 'rgba(250,248,243,0.8)' }} />
                  <span className="text-xs" style={{ color: 'rgba(250,248,243,0.25)' }}>subscribers currently sitting at this stage</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <label className="text-xs" style={{ color: 'rgba(250,248,243,0.4)', minWidth: '60px' }}>Update stage</label>
                <button onClick={() => setManualOverride(o => !o)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-all"
                  style={{ background: manualOverride ? 'rgba(201,147,22,0.1)' : 'rgba(250,248,243,0.04)', border: `1px solid ${manualOverride ? 'rgba(201,147,22,0.35)' : 'rgba(250,248,243,0.07)'}`, color: manualOverride ? '#C99316' : 'rgba(250,248,243,0.3)' }}>
                  <div className="w-3.5 h-3.5 rounded border flex items-center justify-center" style={{ borderColor: manualOverride ? '#C99316' : 'rgba(250,248,243,0.2)', background: manualOverride ? 'rgba(201,147,22,0.2)' : 'transparent' }}>
                    {manualOverride && <div className="w-2 h-2 rounded-sm" style={{ background: '#C99316' }} />}
                  </div>
                  Advance subscriber's stage counter after sending
                </button>
              </div>
              <div className="flex items-center gap-4 pt-1">
                {/* Send mode toggle */}
                <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'rgba(250,248,243,0.08)' }}>
                  <button
                    onClick={() => { setScheduleMode(false); setManualResult(null); }}
                    className="px-3 py-1.5 text-xs transition-all"
                    style={{ background: !scheduleMode ? 'rgba(201,147,22,0.15)' : 'transparent', color: !scheduleMode ? '#C99316' : 'rgba(250,248,243,0.3)', borderRight: '1px solid rgba(250,248,243,0.08)' }}
                    data-testid="send-now-toggle"
                  >
                    Send Now
                  </button>
                  <button
                    onClick={() => { setScheduleMode(true); setManualResult(null); }}
                    className="px-3 py-1.5 text-xs transition-all"
                    style={{ background: scheduleMode ? 'rgba(201,147,22,0.15)' : 'transparent', color: scheduleMode ? '#C99316' : 'rgba(250,248,243,0.3)' }}
                    data-testid="schedule-later-toggle"
                  >
                    Schedule
                  </button>
                </div>

                {scheduleMode ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="relative">
                      <input
                        type="datetime-local"
                        value={scheduleTime}
                        onChange={e => setScheduleTime(e.target.value)}
                        min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                        className="px-3 py-1.5 rounded-lg text-xs"
                        style={{ background: 'rgba(250,248,243,0.06)', border: '1px solid rgba(201,147,22,0.25)', color: 'rgba(250,248,243,0.85)', colorScheme: 'dark' }}
                        data-testid="schedule-datetime-input"
                      />
                    </div>
                    <span className="text-[10px] px-2 py-1 rounded" style={{ background: 'rgba(201,147,22,0.08)', color: 'rgba(201,147,22,0.7)', border: '1px solid rgba(201,147,22,0.15)' }}>
                      London · {londonOffsetLabel}
                    </span>
                    <button
                      onClick={handleScheduleSend}
                      disabled={manualSending || !scheduleTime}
                      className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-medium transition-all disabled:opacity-50"
                      style={{ background: 'rgba(201,147,22,0.15)', border: '1px solid rgba(201,147,22,0.35)', color: '#C99316' }}
                      data-testid="schedule-send-btn"
                    >
                      {manualSending ? <Loader2 size={13} className="animate-spin" /> : <Calendar size={13} />}
                      {manualSending ? 'Scheduling…' : 'Schedule Email'}
                    </button>
                  </div>
                ) : (
                  <button onClick={handleManualSend} disabled={manualSending}
                    className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-medium transition-all disabled:opacity-50"
                    style={{ background: 'rgba(201,147,22,0.15)', border: '1px solid rgba(201,147,22,0.35)', color: '#C99316' }}
                    data-testid="manual-send-btn">
                    {manualSending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    {manualSending ? 'Sending…' : 'Send Email'}
                  </button>
                )}

                {manualResult && (
                  <span className="text-xs" style={{ color: manualResult.ok ? '#4ade80' : '#f87171' }}>
                    {manualResult.ok
                      ? (manualResult.message || `Sent ${manualResult.sent} / ${manualResult.total}${manualResult.failed > 0 ? ` · ${manualResult.failed} failed` : ' · all delivered'}`)
                      : manualResult.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Scheduled Sends Panel */}
          <div className="mt-4 rounded-2xl overflow-hidden" style={{ background: 'rgba(250,248,243,0.03)', border: '1px solid rgba(250,248,243,0.06)' }}>
            <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(250,248,243,0.05)' }}>
              <div className="flex items-center gap-2">
                <Calendar size={13} style={{ color: '#C99316' }} />
                <h2 className="text-sm font-medium" style={{ color: 'rgba(250,248,243,0.8)' }}>Scheduled Sends</h2>
                {scheduledSends.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(201,147,22,0.12)', color: '#C99316' }}>
                    {scheduledSends.filter(s => s.status === 'pending').length} pending
                  </span>
                )}
              </div>
              <button onClick={fetchScheduledSends} disabled={scheduledLoading} className="p-1 rounded transition-colors disabled:opacity-40" style={{ color: 'rgba(250,248,243,0.25)' }}>
                <RefreshCw size={13} className={scheduledLoading ? 'animate-spin' : ''} />
              </button>
            </div>
            <div className="px-5 py-4">
              {scheduledLoading ? (
                <div className="flex items-center justify-center py-6 gap-2" style={{ color: 'rgba(250,248,243,0.15)' }}>
                  <Loader2 size={14} className="animate-spin" /><span className="text-xs">Loading…</span>
                </div>
              ) : scheduledSends.length === 0 ? (
                <p className="text-xs text-center py-4" style={{ color: 'rgba(250,248,243,0.15)' }}>No scheduled sends</p>
              ) : (
                <div className="space-y-2" data-testid="scheduled-sends-list">
                  {scheduledSends.map(s => {
                    const sendDate = new Date(s.send_at);
                    const isPending = s.status === 'pending';
                    const statusColor = { pending: 'rgba(201,147,22,0.7)', sent: '#4ade80', failed: '#f87171' }[s.status] || 'rgba(250,248,243,0.3)';
                    return (
                      <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(250,248,243,0.03)', border: '1px solid rgba(250,248,243,0.05)' }} data-testid={`scheduled-send-${s.id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs truncate" style={{ color: 'rgba(250,248,243,0.65)' }}>{s.email}</p>
                          <p className="text-[11px] mt-0.5" style={{ color: 'rgba(250,248,243,0.25)' }}>
                            Email {s.stage} · {sendDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} at {sendDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0" style={{ color: statusColor, background: `${statusColor}15` }}>{s.status}</span>
                        {isPending && (
                          <button onClick={() => handleCancelScheduled(s.id)} disabled={cancellingId === s.id}
                            className="flex-shrink-0 p-1.5 rounded-lg transition-colors disabled:opacity-40" style={{ color: 'rgba(250,248,243,0.2)' }}
                            data-testid={`cancel-scheduled-${s.id}`}>
                            {cancellingId === s.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ═══ TAB: QUEUE & HEALTH ═══════════════════════════════════════════════ */}
      {activeTab === 'queue' && (
        <div data-testid="queue-health-tab">
          {/* Automation Health */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6" data-testid="automation-health">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-cream/50 text-[10px] font-semibold uppercase tracking-wider">Automation Health</h2>
              <button onClick={fetchHealth} disabled={healthLoading} className="p-1 rounded text-cream/25 hover:text-cream/50 disabled:opacity-40">
                <RefreshCw size={12} className={healthLoading ? 'animate-spin' : ''} />
              </button>
            </div>
            {healthLoading ? (
              <div className="flex items-center gap-2 text-cream/20 text-xs py-4"><Loader2 size={14} className="animate-spin" />Loading…</div>
            ) : health ? (
              <div>
                {/* Status banner */}
                <div className="flex items-center gap-3 mb-5 p-3 rounded-xl border" style={{ borderColor: healthColor[health.status] + '30', background: healthColor[health.status] + '08' }}>
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: healthColor[health.status], boxShadow: health.status === 'healthy' ? '0 0 8px rgba(74,222,128,0.5)' : 'none' }} />
                  <div>
                    <p className="text-sm font-medium" style={{ color: healthColor[health.status] }}>{healthLabel[health.status]}</p>
                    {health.last_run_at && (
                      <p className="text-[11px] mt-0.5" style={{ color: 'rgba(250,248,243,0.3)' }}>
                        Last activity: {fmtDateTime(health.last_run_at)}
                        {health.hours_since_last_run !== null && ` (${health.hours_since_last_run.toFixed(1)}h ago)`}
                      </p>
                    )}
                  </div>
                </div>
                {/* Stat cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Sends (24h)', value: health.sends_24h },
                    { label: 'Delivered (24h)', value: health.sent_24h },
                    { label: 'Failed (24h)', value: health.failed_24h },
                    { label: 'Success Rate', value: health.success_rate_24h + '%' },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-cream/[0.03] border border-cream/[0.06] rounded-lg p-4">
                      <p className="text-cream/25 text-[10px] uppercase tracking-wider mb-1">{label}</p>
                      <p className="text-xl font-serif font-bold text-cream">{value}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-cream/[0.04] flex items-center justify-between">
                  <span className="text-cream/20 text-[10px]">All-time: {health.sent_all_time} delivered of {health.total_all_time} attempts</span>
                  {health.status === 'warning' && (
                    <span className="flex items-center gap-1 text-red-400/70 text-[10px]"><AlertTriangle size={11} />Check cron config in .emergent/crons.yml</span>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-cream/20 text-xs py-4">Unable to load health data.</p>
            )}
          </div>

          {/* Today's Queue */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-6" data-testid="todays-queue">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-cream/50 text-[10px] font-semibold uppercase tracking-wider">Today's Queue</h2>
                <p className="text-cream/20 text-[10px] mt-1">Subscribers who would receive an email if the cron ran right now</p>
              </div>
              <button onClick={fetchQueue} disabled={queueLoading} className="p-1 rounded text-cream/25 hover:text-cream/50 disabled:opacity-40">
                <RefreshCw size={12} className={queueLoading ? 'animate-spin' : ''} />
              </button>
            </div>
            {queueLoading ? (
              <div className="flex items-center gap-2 text-cream/20 text-xs py-8"><Loader2 size={14} className="animate-spin" />Computing queue…</div>
            ) : !queue ? (
              <button onClick={fetchQueue} className="flex items-center gap-2 text-cream/30 text-xs hover:text-cream/60" data-testid="load-queue-btn">
                <Zap size={14} /> Compute today's queue
              </button>
            ) : queue.count === 0 ? (
              <div className="text-center py-8">
                <CheckCircle size={24} className="mx-auto text-emerald-400/40 mb-2" />
                <p className="text-cream/30 text-sm">Queue is empty — all subscribers are up to date</p>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl font-serif font-bold text-gold">{queue.count}</span>
                  <span className="text-cream/30 text-sm">subscribers due for an email</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="queue-table">
                    <thead>
                      <tr className="border-b border-cream/[0.06]">
                        {['Email','Name','Current Stage','Next Email','Last Sent','Next Send'].map(h => (
                          <th key={h} className="text-left text-[10px] uppercase tracking-wider text-cream/20 pb-2 pr-4 font-normal">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {queue.subscribers.map(sub => (
                        <tr key={sub.email} className="border-b border-cream/[0.03] hover:bg-cream/[0.02] transition-colors">
                          <td className="py-2.5 pr-4 font-mono text-cream/50">{sub.email}</td>
                          <td className="py-2.5 pr-4 text-cream/30">{sub.first_name || '—'}</td>
                          <td className="py-2.5 pr-4 tabular-nums" style={{ color: 'rgba(201,147,22,0.6)' }}>{sub.current_stage}</td>
                          <td className="py-2.5 pr-4 text-emerald-400/70">Email {sub.next_stage}</td>
                          <td className="py-2.5 pr-4 text-cream/20">{fmtDate(sub.last_sent_at)}</td>
                          <td className="py-2.5 text-gold/60 text-[11px] tabular-nums">{sub.next_send_at ? fmtDateTime(sub.next_send_at) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {queue.count > queue.subscribers.length && (
                  <p className="text-cream/20 text-[10px] mt-3">Showing first {queue.subscribers.length} of {queue.count} subscribers</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ TAB: EMAIL LIBRARY ═══════════════════════════════════════════════ */}
      {activeTab === 'library' && (
        <div data-testid="email-library-tab">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-cream font-serif text-lg font-semibold">Email Library</h2>
              <p className="text-cream/30 text-xs mt-1">All 50 emails in the Cultural Fluency Development Journey</p>
            </div>
            <button onClick={fetchEmailStats} disabled={emailStatsLoading} className="p-2 rounded-lg border border-cream/10 text-cream/30 hover:text-gold disabled:opacity-40">
              <RefreshCw size={14} className={emailStatsLoading ? 'animate-spin' : ''} />
            </button>
          </div>

          {emailStatsLoading && (
            <div className="py-4 flex items-center gap-2 text-cream/20 text-xs"><Loader2 size={14} className="animate-spin" />Loading send stats…</div>
          )}

          {/* Phase legend */}
          <div className="flex gap-3 mb-4 flex-wrap">
            {[
              { label: 'Phase 1: Bi-weekly (1–12)', color: 'text-blue-400/70', dot: 'bg-blue-400/40' },
              { label: 'Phase 2: Weekly (13–32)', color: 'text-teal-400/70', dot: 'bg-teal-400/40' },
              { label: 'Phase 3: Monthly (33–50)', color: 'text-gold/70', dot: 'bg-gold/40' },
            ].map(({ label, color, dot }) => (
              <span key={label} className={`flex items-center gap-1.5 text-[10px] ${color}`}>
                <span className={`w-2 h-2 rounded-full ${dot}`} />{label}
              </span>
            ))}
          </div>

          <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
            <table className="w-full text-sm" data-testid="email-library-table">
              <thead>
                <tr className="border-b border-gold/[0.04]">
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 w-10">#</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20">Subject</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden sm:table-cell w-24">Phase</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 w-24">Sent</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden md:table-cell w-24">Open Rate</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 w-28">Last sent</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 w-20">Preview</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 50 }, (_, i) => i + 1).map(stage => {
                  const stat = emailStats[stage] || { sent_count: 0, last_sent: null };
                  const phaseColor = getPhaseColor(stage);
                  const phaseLabel = getPhaseLabel(stage);
                  const isLibraryPreviewing = libraryPreview === stage;
                  return (
                    <React.Fragment key={stage}>
                      <tr className={`border-b border-gold/[0.03] hover:bg-cream/[0.01] transition-colors ${isLibraryPreviewing ? 'bg-gold/[0.04]' : ''}`} data-testid={`library-row-${stage}`}>
                        <td className="px-4 py-3">
                          <span className="text-cream/20 text-xs font-mono tabular-nums">{stage}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-cream/60 text-xs leading-snug">{STAGE_SUBJECTS[stage]?.replace(`Email ${stage}: `, '') || '—'}</p>
                        </td>
                        <td className={`px-4 py-3 hidden sm:table-cell text-xs ${phaseColor}`}>{phaseLabel}</td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-cream/40 text-xs tabular-nums font-mono">{stat.sent_count}</span>
                        </td>
                        <td className="px-4 py-3 text-right hidden md:table-cell">
                          {stat.sent_count > 0 ? (
                            <span className={`text-xs tabular-nums font-mono ${
                              (stat.open_rate || 0) >= 30 ? 'text-emerald-400/80' :
                              (stat.open_rate || 0) >= 15 ? 'text-gold/70' :
                              'text-cream/30'
                            }`}>
                              {(stat.open_rate || 0).toFixed(1)}%
                            </span>
                          ) : (
                            <span className="text-cream/15 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-cream/25 text-[11px] tabular-nums">{fmtDate(stat.last_sent)}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setLibraryPreview(isLibraryPreviewing ? null : stage)}
                            className="p-1.5 rounded-lg transition-colors"
                            style={{ color: isLibraryPreviewing ? '#C99316' : 'rgba(250,248,243,0.2)', background: isLibraryPreviewing ? 'rgba(201,147,22,0.1)' : 'transparent' }}
                            data-testid={`library-preview-${stage}`}
                          >
                            <Eye size={12} />
                          </button>
                        </td>
                      </tr>
                      {isLibraryPreviewing && (
                        <tr key={`preview-${stage}`} className="border-b border-gold/[0.04]">
                          <td colSpan={6} className="px-4 py-4 bg-cream/[0.02]">
                            <LibraryEmailPreview stage={stage} headers={headers} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ TAB: DELIVERY LOG ════════════════════════════════════════════════ */}
      {activeTab === 'log' && (
        <div data-testid="delivery-log-tab">
          {/* Email Events Summary */}
          {emailEventsLoading ? (
            <div className="flex items-center gap-2 text-cream/20 text-xs mb-4"><Loader2 size={13} className="animate-spin" /> Loading events…</div>
          ) : emailEvents && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="email-events-summary">
              {[
                { label: 'Total Opens', value: emailEvents.totals?.opened ?? 0, icon: Eye, color: '#4ade80' },
                { label: 'Total Clicks', value: emailEvents.totals?.clicked ?? 0, icon: TrendingUp, color: '#C99316' },
                { label: 'Unique Openers', value: emailEvents.unique_openers ?? 0, icon: Users, color: '#60a5fa' },
                { label: 'Unique Clickers', value: emailEvents.unique_clickers ?? 0, icon: Activity, color: '#a78bfa' },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="bg-navy border border-gold/[0.06] rounded-xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon size={13} style={{ color }} />
                    <span className="text-cream/30 text-[10px] uppercase tracking-wider">{label}</span>
                  </div>
                  <p className="text-xl font-semibold tabular-nums" style={{ color }}>{value}</p>
                </div>
              ))}
            </div>
          )}
          {emailEvents && emailEvents.by_stage && emailEvents.by_stage.length > 0 && (
            <div className="bg-navy border border-gold/[0.06] rounded-xl p-4 mb-5">
              <p className="text-cream/40 text-[10px] uppercase tracking-wider mb-3">Opens &amp; Clicks by Stage</p>
              <div className="flex flex-wrap gap-2">
                {emailEvents.by_stage.map(s => (
                  <div key={s.stage} className="px-3 py-1.5 rounded-lg text-[11px] tabular-nums"
                    style={{ background: 'rgba(250,248,243,0.04)', border: '1px solid rgba(250,248,243,0.08)' }}>
                    <span className="text-cream/30">Stage {s.stage}:</span>
                    <span className="text-emerald-400/70 ml-1.5">{s.opens} open{s.opens !== 1 ? 's' : ''}</span>
                    <span className="text-gold/60 ml-1.5">{s.clicks} click{s.clicks !== 1 ? 's' : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Header row */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-cream font-serif text-lg font-semibold">Delivery Log</h2>
              <p className="text-cream/30 text-xs mt-1">All nurture email send attempts — newest first</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-cream/20 text-xs tabular-nums">{deliveryLogTotal} total</span>
              <button
                onClick={handleLogExportCsv}
                disabled={exportingLog}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider border border-cream/10 rounded-lg text-cream/30 hover:text-gold hover:border-gold/30 transition-colors disabled:opacity-40"
                data-testid="log-export-csv-btn"
              >
                {exportingLog ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
                Export CSV
              </button>
              <button onClick={() => { fetchDeliveryLog(deliveryLogPage); fetchEmailEvents(); }} disabled={deliveryLogLoading} className="p-2 rounded-lg border border-cream/10 text-cream/30 hover:text-gold disabled:opacity-40">
                <RefreshCw size={14} className={deliveryLogLoading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Filter bar */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl px-4 py-3 mb-4 flex flex-wrap items-end gap-3" data-testid="log-filter-bar">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-cream/25 text-[10px] uppercase tracking-wider mb-1">Email</label>
              <input
                type="text"
                value={logEmailFilter}
                onChange={e => setLogEmailFilter(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && fetchDeliveryLog(1, logEmailFilter, logStageFilter, logStatusFilter)}
                placeholder="Filter by email…"
                className="w-full bg-cream/[0.04] border border-cream/10 rounded-lg px-3 py-1.5 text-cream/70 text-xs placeholder-cream/20 focus:outline-none focus:border-gold/30"
                data-testid="log-email-filter"
              />
            </div>
            <div className="w-28">
              <label className="block text-cream/25 text-[10px] uppercase tracking-wider mb-1">Stage</label>
              <input
                type="number"
                min="1"
                max="50"
                value={logStageFilter}
                onChange={e => setLogStageFilter(e.target.value)}
                placeholder="1–50"
                className="w-full bg-cream/[0.04] border border-cream/10 rounded-lg px-3 py-1.5 text-cream/70 text-xs placeholder-cream/20 focus:outline-none focus:border-gold/30"
                data-testid="log-stage-filter"
              />
            </div>
            <div className="w-32">
              <label className="block text-cream/25 text-[10px] uppercase tracking-wider mb-1">Status</label>
              <select
                value={logStatusFilter}
                onChange={e => setLogStatusFilter(e.target.value)}
                className="w-full bg-cream/[0.04] border border-cream/10 rounded-lg px-3 py-1.5 text-cream/70 text-xs focus:outline-none focus:border-gold/30"
                data-testid="log-status-filter"
              >
                <option value="">All statuses</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchDeliveryLog(1, logEmailFilter, logStageFilter, logStatusFilter)}
                disabled={deliveryLogLoading}
                className="px-4 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                style={{ background: 'rgba(201,147,22,0.15)', border: '1px solid rgba(201,147,22,0.35)', color: '#C99316' }}
                data-testid="log-apply-filter-btn"
              >
                Apply
              </button>
              {(logEmailFilter || logStageFilter !== '' || logStatusFilter) && (
                <button
                  onClick={() => {
                    setLogEmailFilter('');
                    setLogStageFilter('');
                    setLogStatusFilter('');
                    fetchDeliveryLog(1, '', '', '');
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs text-cream/30 hover:text-cream/60 border border-cream/10 transition-colors"
                  data-testid="log-clear-filter-btn"
                >
                  Clear
                </button>
              )}
            </div>
            {(logEmailFilter || logStageFilter !== '' || logStatusFilter) && (
              <span className="text-[10px] text-gold/60 ml-1">Filtered</span>
            )}
          </div>

          {deliveryLogLoading && deliveryLog.length === 0 ? (
            <div className="py-12 flex items-center justify-center text-cream/20 gap-2"><Loader2 size={18} className="animate-spin" /><span className="text-sm">Loading log…</span></div>
          ) : (
            <div className="bg-navy border border-gold/[0.06] rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="delivery-log-table">
                <thead>
                  <tr className="border-b border-gold/[0.04]">
                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20">Email</th>
                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 w-16">Stage</th>
                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 w-20">Status</th>
                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden md:table-cell">Subject</th>
                    <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-cream/20 hidden sm:table-cell w-36">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryLog.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-cream/20 text-sm">
                        {(logEmailFilter || logStageFilter !== '' || logStatusFilter) ? 'No entries match your filters' : 'No send log entries yet'}
                      </td>
                    </tr>
                  ) : deliveryLog.map((entry, idx) => (
                    <tr key={entry.id || idx} className="border-b border-gold/[0.03] hover:bg-cream/[0.01] transition-colors" data-testid={`log-row-${idx}`}>
                      <td className="px-4 py-2.5 font-mono text-cream/50 text-xs">{entry.subscriber_email}</td>
                      <td className="px-4 py-2.5 tabular-nums text-xs" style={{ color: 'rgba(201,147,22,0.6)' }}>{entry.stage}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          entry.status === 'sent' ? 'bg-emerald-500/10 text-emerald-400' :
                          entry.status === 'skipped' ? 'bg-cream/5 text-cream/30' :
                          'bg-red-500/10 text-red-400'
                        }`}>
                          {entry.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-cream/30 text-xs hidden md:table-cell truncate max-w-xs">{entry.subject || '—'}</td>
                      <td className="px-4 py-2.5 text-cream/20 text-[11px] tabular-nums hidden sm:table-cell text-right">{fmtDateTime(entry.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination */}
              {deliveryLogTotal > 50 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gold/[0.04]">
                  <p className="text-cream/15 text-xs tabular-nums">{deliveryLogTotal} entries · Page {deliveryLogPage} of {Math.ceil(deliveryLogTotal / 50)}</p>
                  <div className="flex gap-1">
                    <button onClick={() => fetchDeliveryLog(Math.max(1, deliveryLogPage - 1), logEmailFilter, logStageFilter, logStatusFilter)} disabled={deliveryLogPage === 1 || deliveryLogLoading}
                      className="p-1.5 border border-cream/10 rounded text-cream/30 hover:text-gold disabled:opacity-30 transition-colors" data-testid="log-prev-page">
                      <ChevronLeft size={14} />
                    </button>
                    <button onClick={() => fetchDeliveryLog(Math.min(Math.ceil(deliveryLogTotal / 50), deliveryLogPage + 1), logEmailFilter, logStageFilter, logStatusFilter)} disabled={deliveryLogPage >= Math.ceil(deliveryLogTotal / 50) || deliveryLogLoading}
                      className="p-1.5 border border-cream/10 rounded text-cream/30 hover:text-gold disabled:opacity-30 transition-colors" data-testid="log-next-page">
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ TAB: SETTINGS ════════════════════════════════════════════════════ */}
      {activeTab === 'settings' && (
        <div data-testid="settings-tab">
          {/* Phase Configuration */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-6 mb-6">
            <div className="mb-5">
              <h2 className="text-cream font-serif text-lg font-semibold">Phase Boundaries</h2>
              <p className="text-cream/30 text-xs mt-1">
                Configure when each phase of the nurture sequence ends. These labels are stored in the database and displayed in the Email Library.
                Note: The actual cadence (days between emails) is defined in <code className="text-gold/60">nurture_constants.py</code> and requires a code change to modify.
              </p>
            </div>
            {!phaseConfig ? (
              <div className="flex items-center gap-2 text-cream/20 text-xs py-4"><Loader2 size={14} className="animate-spin" />Loading config…</div>
            ) : phaseConfigForm && (
              <div className="space-y-4 max-w-lg">
                {[
                  { key: 'phase_1_label', label: 'Phase 1 Label', hint: 'Stages 1–12 (bi-weekly)' },
                  { key: 'phase_2_label', label: 'Phase 2 Label', hint: 'Stages 13–32 (weekly)' },
                  { key: 'phase_3_label', label: 'Phase 3 Label', hint: 'Stages 33–50 (monthly)' },
                ].map(({ key, label, hint }) => (
                  <div key={key}>
                    <label className="block text-cream/30 text-[10px] uppercase tracking-wider mb-1.5">{label}</label>
                    <input
                      value={phaseConfigForm[key] || ''}
                      onChange={e => setPhaseConfigForm(prev => ({ ...prev, [key]: e.target.value }))}
                      className="w-full bg-navy-deep border border-cream/10 rounded-lg px-4 py-2.5 text-cream text-sm focus:outline-none focus:border-gold/30"
                      placeholder={hint}
                      data-testid={`phase-config-${key}`}
                    />
                    <p className="text-cream/15 text-[10px] mt-1">{hint}</p>
                  </div>
                ))}
                <div className="flex items-center gap-3 pt-2">
                  <button onClick={handleSavePhaseConfig} disabled={savingConfig}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-medium transition-all disabled:opacity-50"
                    style={{ background: 'rgba(201,147,22,0.15)', border: '1px solid rgba(201,147,22,0.35)', color: '#C99316' }}
                    data-testid="save-phase-config-btn">
                    {savingConfig ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                    {savingConfig ? 'Saving…' : 'Save Phase Labels'}
                  </button>
                  {configSaved && <span className="text-emerald-400 text-xs flex items-center gap-1"><CheckCircle size={12} /> Saved</span>}
                </div>
              </div>
            )}
          </div>

          {/* Subscriber Reconciliation */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-6">
            <div className="mb-5">
              <h2 className="text-cream font-serif text-lg font-semibold">Subscriber Reconciliation</h2>
              <p className="text-cream/30 text-xs mt-1">
                Ensure all confirmed subscribers have <code className="text-gold/60">nurture_enrolled_at</code> set.
                This is safe to run at any time — it <strong className="text-cream/60">does not</strong> reset stages or sequences.
              </p>
            </div>
            {reconcileResult ? (
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm mb-4" data-testid="reconcile-result">
                <p className="font-medium">{reconcileResult.reconciled} subscriber(s) updated</p>
                {reconcileResult.stage_0_staggered > 0 && (
                  <p className="text-xs mt-1 text-emerald-400/70">{reconcileResult.stage_0_staggered} stage-0 subscribers staggered 10/day from tomorrow</p>
                )}
                {reconcileResult.in_sequence_backfilled > 0 && (
                  <p className="text-xs mt-0.5 text-emerald-400/70">{reconcileResult.in_sequence_backfilled} in-sequence subscribers backfilled from last send date</p>
                )}
                <p className="text-xs mt-1 text-emerald-400/50">{reconcileResult.message}</p>
              </div>
            ) : null}
            <button onClick={handleReconcile} disabled={reconcileLoading || !!reconcileResult}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-medium transition-all disabled:opacity-50"
              style={{ background: 'rgba(250,248,243,0.05)', border: '1px solid rgba(250,248,243,0.12)', color: 'rgba(250,248,243,0.6)' }}
              data-testid="reconcile-btn">
              {reconcileLoading ? <Loader2 size={13} className="animate-spin" /> : <Database size={13} />}
              {reconcileLoading ? 'Reconciling…' : reconcileResult ? 'Reconciliation complete' : 'Run Reconciliation'}
            </button>
          </div>

          {/* Re-engagement */}
          <div className="bg-navy border border-gold/[0.06] rounded-xl p-6">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h2 className="text-cream font-serif text-lg font-semibold">Re-engagement</h2>
                <p className="text-cream/30 text-xs mt-1">
                  Re-queue subscribers inactive for 90+ days so they receive their next email at the next cron fire.
                  No email is sent immediately — they rejoin the normal sequence.
                </p>
              </div>
              <button onClick={fetchInactiveStats} disabled={inactiveLoading} className="p-2 rounded-lg border border-cream/10 text-cream/30 hover:text-gold disabled:opacity-40">
                <RefreshCw size={13} className={inactiveLoading ? 'animate-spin' : ''} />
              </button>
            </div>

            {inactiveLoading ? (
              <div className="flex items-center gap-2 text-cream/20 text-xs"><Loader2 size={13} className="animate-spin" />Loading…</div>
            ) : inactiveStats ? (
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { label: 'Never started', value: inactiveStats.never_sent_count, color: 'rgba(250,248,243,0.35)' },
                  { label: 'Inactive 90+ days', value: inactiveStats.inactive_count, color: '#fb923c' },
                  { label: 'Total eligible', value: inactiveStats.total_eligible, color: '#C99316' },
                ].map(({ label, value, color }) => (
                  <div key={label} className="rounded-xl p-3" style={{ background: 'rgba(250,248,243,0.03)', border: '1px solid rgba(250,248,243,0.07)' }}>
                    <p className="text-[10px] uppercase tracking-wider text-cream/25 mb-1">{label}</p>
                    <p className="text-2xl font-semibold tabular-nums" style={{ color }}>{value}</p>
                  </div>
                ))}
              </div>
            ) : null}

            {reactivateResult ? (
              <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm mb-4" data-testid="reactivate-result">
                <p className="font-medium">{reactivateResult.reactivated} subscriber(s) re-queued</p>
                {reactivateResult.emails_sent > 0 && <p className="text-xs mt-1 text-blue-400/70">{reactivateResult.emails_sent} re-engagement email(s) sent immediately</p>}
                {reactivateResult.inactive_requeued > 0 && <p className="text-xs mt-0.5 text-blue-400/70">{reactivateResult.inactive_requeued} inactive in-sequence subscribers</p>}
                {reactivateResult.never_started_queued > 0 && <p className="text-xs mt-0.5 text-blue-400/70">{reactivateResult.never_started_queued} never-started subscribers</p>}
                <p className="text-xs mt-1 text-blue-400/50">{reactivateResult.message}</p>
              </div>
            ) : null}

            <button onClick={handleReactivate} disabled={reactivateLoading || !!reactivateResult || !inactiveStats || inactiveStats.total_eligible === 0}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-medium transition-all disabled:opacity-50"
              style={{ background: 'rgba(96,165,250,0.07)', border: '1px solid rgba(96,165,250,0.2)', color: 'rgba(96,165,250,0.8)' }}
              data-testid="reactivate-btn">
              {reactivateLoading ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />}
              {reactivateLoading ? 'Re-queuing…' : reactivateResult ? 'Re-engagement queued' : `Re-activate ${inactiveStats?.total_eligible ?? '…'} subscribers`}
            </button>
          </div>
        </div>
      )}
    </div>

    {/* Social Publish Modal */}
    {socialModal.open && (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6"
        style={{ background: 'rgba(4,10,20,0.92)', backdropFilter: 'blur(10px)' }}
        onClick={e => { if (e.target === e.currentTarget) closeSocialModal(); }}
        data-testid="social-modal">
        <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{ background: '#071B3A', border: '1px solid rgba(201,147,22,0.14)', boxShadow: '0 32px 80px rgba(0,0,0,0.6)' }}>
          <div className="relative">
            <img src={TCL_BANNER} alt="Third Culture Leadership" className="w-full object-cover" style={{ height: '88px', objectPosition: 'center 30%' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(7,27,58,0) 20%, rgba(7,27,58,0.95) 100%)' }} />
            <button onClick={closeSocialModal} className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:bg-white/20" style={{ background: 'rgba(0,0,0,0.45)' }} data-testid="social-modal-close">
              <XCircle size={15} style={{ color: 'rgba(250,248,243,0.7)' }} />
            </button>
            <div className="absolute bottom-2.5 left-4">
              <p className="text-[9px] uppercase tracking-[0.22em] font-sans" style={{ color: 'rgba(201,147,22,0.7)' }}>Stage {previewStage} · Third Culture Leadership</p>
            </div>
          </div>
          <div className="px-5 pt-3 pb-3" style={{ borderBottom: '1px solid rgba(250,248,243,0.05)' }}>
            <p className="text-sm font-medium leading-snug" style={{ color: 'rgba(250,248,243,0.7)' }}>{socialModal.subject}</p>
          </div>
          <div className="px-5 pt-4 pb-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[9px] uppercase tracking-[0.2em] font-sans" style={{ color: 'rgba(250,248,243,0.22)' }}>Post text — edit before publishing</label>
              <button onClick={handleCopyText} className="inline-flex items-center gap-1.5 text-[11px] transition-colors" style={{ color: copiedText ? '#C99316' : 'rgba(250,248,243,0.25)' }} data-testid="social-modal-copy">
                {copiedText ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
              </button>
            </div>
            <textarea value={socialModal.text} onChange={e => setSocialModal(m => ({ ...m, text: e.target.value }))} rows={8} className="w-full rounded-xl px-4 py-3 text-sm resize-none focus:outline-none transition-colors"
              style={{ background: 'rgba(250,248,243,0.035)', border: '1px solid rgba(250,248,243,0.07)', color: 'rgba(250,248,243,0.72)', lineHeight: '1.65', scrollbarWidth: 'thin' }}
              data-testid="social-modal-textarea" />
            <p className="text-right text-[10px] mt-1" style={{ color: 'rgba(250,248,243,0.18)' }}>{socialModal.text.length} chars</p>
          </div>
          <div className="px-5 pb-3">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <button onClick={handlePublishLinkedIn} className="flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-semibold text-sm transition-all hover:opacity-92 active:scale-[0.97]" style={{ background: '#0A66C2', color: '#fff' }} data-testid="publish-linkedin-btn">
                <Linkedin size={17} />Post to LinkedIn
              </button>
              <button onClick={handlePublishX} className="flex items-center justify-center gap-2.5 py-3.5 rounded-xl font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.97]" style={{ background: '#000', color: '#fff', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="publish-x-btn">
                <X size={17} />Post to X
              </button>
            </div>
            <button onClick={handleSaveDraftCMS} disabled={savingDraft || savedDraft} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-medium transition-all disabled:opacity-60"
              style={{ border: '1px solid rgba(250,248,243,0.07)', color: savedDraft ? '#C99316' : 'rgba(250,248,243,0.3)', background: savedDraft ? 'rgba(201,147,22,0.06)' : 'transparent' }}
              data-testid="save-draft-cms-btn">
              {savingDraft ? <><Loader2 size={12} className="animate-spin" /> Saving to Social Hub…</> : savedDraft ? <><Check size={12} /> Saved to Social Hub</> : <><Send size={12} /> Save as draft in Social Hub</>}
            </button>
            {savedDraft && (
              <button onClick={() => { closeSocialModal(); navigate('/admin/social'); }} className="mt-2 w-full flex items-center justify-center gap-1.5 text-xs transition-colors hover:opacity-80" style={{ color: 'rgba(201,147,22,0.5)' }}>
                <ExternalLink size={11} />View in Social Hub
              </button>
            )}
          </div>
          <div className="px-5 pb-5 pt-1" style={{ borderTop: '1px solid rgba(250,248,243,0.04)' }}>
            <p className="text-[10px] text-center" style={{ color: 'rgba(250,248,243,0.14)' }}>"Post to LinkedIn" and "Post to X" open each platform in a new tab with the text pre-filled.</p>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

/* ── Inline email preview for library ── */
const LibraryEmailPreview = ({ stage, headers }) => {
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    fetch(`${API_URL}/api/cfq/nurture/preview/${stage}`, { headers, credentials: 'include' })
      .then(r => r.ok ? r.text() : null)
      .then(h => { if (h) setHtml(h); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <div className="flex items-center gap-2 text-cream/20 text-xs py-4"><Loader2 size={12} className="animate-spin" />Loading preview…</div>;
  if (!html) return <p className="text-cream/20 text-xs">Preview not available</p>;
  return <iframe srcDoc={html} title={`Preview Email ${stage}`} className="w-full rounded-lg border border-cream/[0.06]" style={{ height: '500px', border: 'none' }} />;
};

export default AdminNurture;
